import React, { useMemo, useState } from "react";
import { Button, Tooltip as ChakraTooltip } from "@chakra-ui/react";
import { LoadingSpinner } from "components/Loading/LoadingSpinner";
import {
	useAllRoleMatrices,
	useModules,
	useUpdateAnyRoleMatrix,
} from "newui/features/RolesPermissions/hooks/useRoleMatrix";
import {
	EDITABLE_PERMISSION_LEVEL_CYCLE,
	RBAC_PRIVILEGE_CLAMPED_ERROR_CODE,
	type ModuleResource,
	type PermissionLevel,
	type RoleMatrixEntry,
	type RoleResource,
} from "newui/features/RolesPermissions/types/RolesPermissions.type";
import { showToast } from "utils/helpers";

export interface PermissionMatrixEditorProps {
	highlightedRoleId?: number;
	roles: RoleResource[] | undefined;
	// When a role is selected the matrix collapses to just that role's column;
	// this clears the selection to show every role again.
	onClearSelection?: () => void;
}

interface LevelConfig {
	// Text colour used for the cell label.
	className: string;
	// Single fill (background + border) used by BOTH the legend swatch and the
	// cell, so the legend colour always matches the cell colour exactly.
	fillClassName: string;
	label: string;
	shortLabel: string;
	tooltip?: string;
}

interface FocusedCell {
	moduleId: number;
	roleId: number;
}

// "Event-scoped"/"E" must never render bare — always paired with the plain
// language label below and, where space allows, the explanatory tooltip.
// Each level is colour-coded by FILLING both the legend swatch and the matrix
// cell with the same tint (plus matching label text), so a glance down a column
// reads as colour, not just words.
const LEVEL_CONFIG: Record<PermissionLevel, LevelConfig> = {
	full: {
		label: "Full",
		shortLabel: "Full",
		className: "text-emerald-800",
		fillClassName: "bg-emerald-100 border-emerald-300",
	},
	view: {
		label: "View",
		shortLabel: "View",
		className: "text-blue-800",
		fillClassName: "bg-blue-100 border-blue-300",
	},
	comment: {
		label: "Comment",
		shortLabel: "Comment",
		className: "text-amber-900",
		fillClassName: "bg-amber-100 border-amber-300",
	},
	event: {
		label: "Event only",
		shortLabel: "Event only",
		className: "text-slate-700",
		fillClassName: "bg-slate-100 border-slate-300",
		tooltip:
			"Access is limited to specific RFx events this person is invited to — not the whole project.",
	},
	none: {
		label: "No access",
		shortLabel: "No access",
		className: "text-gray-400",
		fillClassName: "bg-gray-50 border-gray-200",
	},
};

const nextLevel = (current: PermissionLevel): PermissionLevel => {
	// "event" isn't in the editable cycle (display-only) — cycling from it
	// starts back at the top of the editable sequence.
	const index = EDITABLE_PERMISSION_LEVEL_CYCLE.indexOf(current);

	return EDITABLE_PERMISSION_LEVEL_CYCLE[(index + 1) % EDITABLE_PERMISSION_LEVEL_CYCLE.length];
};

// Local, per-role edit overrides keyed by moduleId. Cleared for a role once
// that role's PUT succeeds; kept on failure so the user can retry/adjust.
type RoleEdits = Map<number, PermissionLevel>;

// Whether a module (by its applies_to) is relevant to a given role side. Mirrors
// the backend Module::scopeApplicableToSide: procurer/bidder see their own side +
// 'both'; hybrid/unknown see everything. Used to trim the single-role viewer to
// just the modules that apply to that role's side (no cross-side "No access" noise).
const moduleAppliesToRoleSide = (appliesTo: string, side: string): boolean => {
	if (side === "procurer") {
		return appliesTo === "procurer" || appliesTo === "both";
	}
	if (side === "bidder") {
		return appliesTo === "bidder" || appliesTo === "both";
	}
	return true;
};

export const PermissionMatrixEditor = ({ roles, highlightedRoleId, onClearSelection }: PermissionMatrixEditorProps) => {
	const { data: modules, isLoading: isLoadingModules } = useModules();
	const { matricesByRoleId, isLoading: isLoadingMatrices } = useAllRoleMatrices(roles);
	const updateMatrixMutation = useUpdateAnyRoleMatrix();

	const [focusedCell, setFocusedCell] = useState<FocusedCell | null>(null);
	// Crosshair hover: highlights the hovered cell's row + column and outlines the
	// intersecting cell, so it's easy to trace which role×module you're on.
	const [hoveredCell, setHoveredCell] = useState<FocusedCell | null>(null);
	const [editsByRoleId, setEditsByRoleId] = useState<Map<number, RoleEdits>>(new Map());
	const [savingRoleIds, setSavingRoleIds] = useState<Set<number>>(new Set());

	const safeRoles = useMemo(() => roles ?? [], [roles]);
	const safeModules = useMemo(() => modules ?? [], [modules]);

	// #1/#1.1: selecting a role on the left collapses the matrix to just that
	// role's column (and highlights it), so a single role is easy to read/edit.
	// No selection → every role shown. An unknown id falls back to all roles.
	const selectedRole = useMemo(
		() => safeRoles.find((role) => role.id === highlightedRoleId) ?? null,
		[safeRoles, highlightedRoleId]
	);
	const visibleRoles = useMemo(
		() => (selectedRole ? [selectedRole] : safeRoles),
		[selectedRole, safeRoles]
	);
	const isCollapsed = selectedRole !== null && safeRoles.length > 1;

	// Viewer (single focused role): show only the modules that apply to that role's
	// side — a bidder role hides procurer-only modules and vice versa (hybrid shows
	// both). The full multi-role matrix keeps every company-side module (rows are
	// shared across differently-sided role columns, so they can't be side-trimmed).
	const displayModules = useMemo(
		() =>
			selectedRole
				? safeModules.filter((module) =>
						moduleAppliesToRoleSide(module.applies_to, selectedRole.side)
					)
				: safeModules,
		[safeModules, selectedRole]
	);

	const entriesByRoleAndModule = useMemo(() => {
		const map = new Map<number, Map<number, RoleMatrixEntry>>();
		safeRoles.forEach((role) => {
			const entries = matricesByRoleId.get(role.id)?.entries ?? [];
			const entryMap = new Map<number, RoleMatrixEntry>();
			entries.forEach((entry) => {
				entryMap.set(entry.module_id, entry);
			});
			map.set(role.id, entryMap);
		});
		return map;
	}, [safeRoles, matricesByRoleId]);

	const getEffectiveLevel = (roleId: number, moduleId: number): PermissionLevel => {
		const localOverride = editsByRoleId.get(roleId)?.get(moduleId);
		if (localOverride) {
			return localOverride;
		}

		return entriesByRoleAndModule.get(roleId)?.get(moduleId)?.level ?? "none";
	};

	const isRoleDirty = (roleId: number): boolean => {
		const edits = editsByRoleId.get(roleId);
		return !!edits && edits.size > 0;
	};

	const handleCellClick = (role: RoleResource, module: ModuleResource) => {
		if (role.is_base || !module.available) {
			return;
		}

		if (!focusedCell || focusedCell.roleId !== role.id || focusedCell.moduleId !== module.id) {
			setFocusedCell({ roleId: role.id, moduleId: module.id });
			return;
		}

		const currentLevel = getEffectiveLevel(role.id, module.id);
		setCellLevel(role.id, module.id, nextLevel(currentLevel));
	};

	const setCellLevel = (roleId: number, moduleId: number, level: PermissionLevel) => {
		setEditsByRoleId((prev) => {
			const next = new Map(prev);
			const roleEdits = new Map(next.get(roleId) ?? []);
			roleEdits.set(moduleId, level);
			next.set(roleId, roleEdits);
			return next;
		});
	};

	const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
		if (!focusedCell) {
			return;
		}

		const roleIndex = visibleRoles.findIndex((role) => role.id === focusedCell.roleId);
		const moduleIndex = displayModules.findIndex((module) => module.id === focusedCell.moduleId);

		if (roleIndex === -1 || moduleIndex === -1) {
			return;
		}

		if (event.key === "ArrowDown") {
			const nextModule = displayModules[Math.min(displayModules.length - 1, moduleIndex + 1)];
			setFocusedCell({ roleId: focusedCell.roleId, moduleId: nextModule.id });
			event.preventDefault();
		} else if (event.key === "ArrowUp") {
			const prevModule = displayModules[Math.max(0, moduleIndex - 1)];
			setFocusedCell({ roleId: focusedCell.roleId, moduleId: prevModule.id });
			event.preventDefault();
		} else if (event.key === "ArrowRight") {
			const nextRole = visibleRoles[Math.min(visibleRoles.length - 1, roleIndex + 1)];
			setFocusedCell({ roleId: nextRole.id, moduleId: focusedCell.moduleId });
			event.preventDefault();
		} else if (event.key === "ArrowLeft") {
			const prevRole = visibleRoles[Math.max(0, roleIndex - 1)];
			setFocusedCell({ roleId: prevRole.id, moduleId: focusedCell.moduleId });
			event.preventDefault();
		}
	};

	const handleSaveChanges = () => {
		const dirtyRoles = safeRoles.filter((role) => isRoleDirty(role.id));

		dirtyRoles.forEach((role) => {
			const roleEdits = editsByRoleId.get(role.id) ?? new Map<number, PermissionLevel>();
			const entryMap = entriesByRoleAndModule.get(role.id) ?? new Map<number, RoleMatrixEntry>();

			const entries = safeModules.map((module) => {
				const existingEntry = entryMap.get(module.id);
				const level = roleEdits.get(module.id) ?? existingEntry?.level ?? "none";
				return { module_id: module.id, level };
			});

			setSavingRoleIds((prev) => new Set(prev).add(role.id));

			updateMatrixMutation.mutate(
				{ roleId: role.id, payload: { entries } },
				{
					onSuccess: (data) => {
						setSavingRoleIds((prev) => {
							const next = new Set(prev);
							next.delete(role.id);
							return next;
						});
						setEditsByRoleId((prev) => {
							const next = new Map(prev);
							next.delete(role.id);
							return next;
						});

						// H6: the matrix PUT clamps silently — a 200 with reduced levels
						// and clamped:true per entry, never a 422. Surface that here;
						// the reduced levels are still applied as server truth.
						if (data.entries.some((entry) => entry.clamped)) {
							showToast(
								`Some levels for ${role.name} were reduced to your allowed maximum.`,
								"Permission levels adjusted",
								false,
								`rbac-matrix-clamped-${role.id}`
							);
						}
					},
					onError: (error: unknown) => {
						setSavingRoleIds((prev) => {
							const next = new Set(prev);
							next.delete(role.id);
							return next;
						});

						const errorResponse = (error as { response?: { data?: { error?: string }; status?: number; } })
							?.response;

						// Defensive only: the matrix endpoint clamps via a 200 (see
						// onSuccess above), never this 422 — that shape belongs to 008's
						// assignment endpoints. Kept in case that ever changes. The
						// backend's `error` field is a flat machine code, not
						// `{code, message}` — this narrow was previously dead.
						if (
							errorResponse?.status === 422 &&
							errorResponse?.data?.error === RBAC_PRIVILEGE_CLAMPED_ERROR_CODE
						) {
							showToast(
								`The changes for ${role.name} would exceed the allowed privilege level for this role and were rejected.`,
								"Permission not applied",
								false,
								`rbac-privilege-clamped-${role.id}`
							);
							return;
						}

						showToast(
							`Unable to update permissions for ${role.name}. Please try again.`,
							"Update failed",
							false,
							`rbac-update-error-${role.id}`
						);
					},
				}
			);
		});
	};

	if (isLoadingModules || isLoadingMatrices) {
		return <LoadingSpinner label="Loading permission matrix..." verticalPadding={8} />;
	}

	const anyDirty = safeRoles.some((role) => isRoleDirty(role.id));
	const isSaving = savingRoleIds.size > 0;

	return (
		<div data-testid="permission-matrix-editor">
			<div className="mb-4 flex flex-wrap items-center gap-x-4 gap-y-2">
				{/* #6: Save changes sits to the LEFT of the legend. */}
				<Button
					data-testid="save-matrix-changes"
					size="sm"
					colorScheme="blue"
					isDisabled={!anyDirty}
					isLoading={isSaving}
					onClick={handleSaveChanges}
				>
					Save changes
				</Button>

				<div className="flex flex-wrap items-center gap-4 text-xs text-gray-600" data-testid="matrix-legend">
					{(Object.keys(LEVEL_CONFIG) as PermissionLevel[]).map((level) => {
						const config = LEVEL_CONFIG[level];
						const legendItem = (
							<span key={level} className="flex items-center gap-1.5" data-testid={`legend-${level}`}>
								<span className={`h-3 w-3 rounded-sm border ${config.fillClassName}`} />
								{config.label}
							</span>
						);

						if (config.tooltip) {
							return (
								<ChakraTooltip key={level} label={config.tooltip} hasArrow>
									{legendItem}
								</ChakraTooltip>
							);
						}

						return legendItem;
					})}
					<span className="flex items-center gap-1.5">
						<span className="h-3 w-3 rounded-sm border border-gray-300 bg-[repeating-linear-gradient(135deg,#f4f5f7,#f4f5f7_4px,#e9ecf1_4px,#e9ecf1_8px)]" />
						Clamped by subscription
					</span>
				</div>
			</div>

			{isCollapsed && (
				<div
					className="mb-3 flex items-center gap-3 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-800"
					data-testid="matrix-collapsed-banner"
				>
					{onClearSelection && (
						<button
							type="button"
							onClick={onClearSelection}
							className="whitespace-nowrap font-medium text-blue-700 underline hover:text-blue-900"
							data-testid="matrix-show-all-roles"
						>
							Show all roles
						</button>
					)}
					<span>
						Showing <span className="font-semibold">{selectedRole?.name}</span> only.
						{selectedRole?.is_base
							? " System roles are read-only."
							: " Click a cell to change its level, then Save changes."}
					</span>
				</div>
			)}

			<div
				className="max-h-[70vh] overflow-auto rounded-md border border-gray-200"
				tabIndex={0}
				onKeyDown={handleKeyDown}
				onMouseLeave={() => setHoveredCell(null)}
				data-testid="matrix-sheet-wrap"
			>
				<table className={`${isCollapsed ? "" : "min-w-full"} border-separate border-spacing-0 text-sm`}>
					<thead>
						<tr>
							<th className="sticky left-0 top-0 z-20 min-w-[220px] border-b border-r border-gray-200 bg-gray-50 px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
								Module
							</th>
							{visibleRoles.map((role) => {
								const colHovered = hoveredCell?.roleId === role.id;
								const headerBg = highlightedRoleId === role.id
									? "bg-blue-100 text-blue-800"
									: colHovered
										? "bg-blue-50 text-blue-800"
										: "bg-gray-50";
								return (
								<th
									key={role.id}
									data-testid={`matrix-column-header-${role.id}`}
									className={`sticky top-0 z-10 min-w-[140px] border-b border-r border-gray-200 px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 ${headerBg}`}
								>
									{role.name}
									{isRoleDirty(role.id) && (
										<span className="ml-1 text-blue-600" data-testid={`role-dirty-indicator-${role.id}`}>
											•
										</span>
									)}
								</th>
							);
							})}
						</tr>
					</thead>
					<tbody>
						{displayModules.map((module, rowIndex) => {
							const rowHovered = hoveredCell?.moduleId === module.id;
							// Explicit OPAQUE stripe (was even:bg-gray-50/60 — a translucent fill that let the
							// scrolling columns show THROUGH the frozen first column).
							const rowBg = rowHovered ? "bg-blue-50" : rowIndex % 2 === 1 ? "bg-gray-50" : "bg-white";
							return (
							<tr key={module.id} className={rowBg}>
								<th className={`sticky left-0 z-[1] border-b border-r border-gray-200 px-4 py-2 text-left font-medium text-gray-900 ${rowBg}`}>
									{module.name}
								</th>
								{visibleRoles.map((role) => {
									const level = getEffectiveLevel(role.id, module.id);
									const config = LEVEL_CONFIG[level];
									const isFocused =
										focusedCell?.roleId === role.id && focusedCell?.moduleId === module.id;
									const colHovered = hoveredCell?.roleId === role.id;
									const isHovered = rowHovered && colHovered;
									// Subscription-unavailable is a MODULE fact (ModuleResource.available),
									// not entry.clamped — GET always returns clamped:false (011's
									// matrixFor() only computes it relative to a PUT request), so keying
									// this off the entry would never render the striped/disabled state.
									const unavailable = !module.available;
									const isEditable = !role.is_base && module.available;

									const cellContent = (
										<div
											data-testid={`matrix-cell-${role.id}-${module.id}`}
											role={isEditable ? "button" : undefined}
											tabIndex={isEditable ? 0 : -1}
											aria-disabled={!isEditable}
											onClick={() => handleCellClick(role, module)}
											onMouseEnter={() => setHoveredCell({ roleId: role.id, moduleId: module.id })}
											onKeyDown={(e) => {
												if (isEditable && (e.key === "Enter" || e.key === " ")) {
													handleCellClick(role, module);
												}
											}}
											className={`flex h-10 items-center gap-2 px-4 text-sm font-semibold ${unavailable
												? "cursor-not-allowed bg-[repeating-linear-gradient(135deg,#f4f5f7,#f4f5f7_6px,#e9ecf1_6px,#e9ecf1_12px)] text-gray-400"
												: `${config.fillClassName} ${config.className} ${isEditable ? "cursor-pointer" : "cursor-default"
												}`
												} ${isHovered ? "outline outline-2 -outline-offset-2 outline-blue-400" : isFocused ? "outline outline-2 -outline-offset-2 outline-blue-500" : ""}`}
										>
											{config.shortLabel}
										</div>
									);

									return (
										<td
											key={role.id}
											className={`border-b border-r border-gray-200 p-0 ${highlightedRoleId === role.id || colHovered ? "bg-blue-50/40" : ""
												}`}
										>
											{config.tooltip ? (
												<ChakraTooltip label={config.tooltip} hasArrow>
													{cellContent}
												</ChakraTooltip>
											) : unavailable ? (
												<ChakraTooltip label="This module is not included in the current subscription tier for this role." hasArrow>
													{cellContent}
												</ChakraTooltip>
											) : (
												cellContent
											)}
										</td>
									);
								})}
							</tr>
							);
						})}
					</tbody>
				</table>
			</div>
		</div>
	);
};
